import { Backdrop, Box, CircularProgress, Typography } from "@material-ui/core";
import { Card } from "common/components/Card";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { IApplicationState } from "redux/root-reducer";
import styles from "./OverviewCard.module.scss";
import {
  IOverviewProgress,
  OverviewGoalProgress,
} from "./OverviewGoalProgress";
import { createSelector } from "reselect";
import { useDispatch, useSelector } from "react-redux";
import { t } from "i18next";
import { dataInterval, DeviceType } from "common/types";
import { setSelectedInstallation } from "nodeDetails/nodeDetails.actions";
import {
  getDataForPrognosis,
  getDisplayNameOnDeviceType,
  getPrognosis,
  getSumOfDataset,
  groupInstallations,
  isInstallationSensor,
} from "../../functions/dataFunctions";
import moment from "moment";
import {
  getCurrentMonthUnixTimeObject,
  longMonthNames,
} from "nodeDetails/functions/dateFunctions";
import InfoIcon from "@material-ui/icons/Info";
import { OverviewInformationModal } from "./OverviewInformationModal";

const getNodeSelector = createSelector(
  (state: IApplicationState) => state,
  (state) => ({
    currency: state.common.currentUserInfo.DomainSettings.Currency,
    isFetching: state.node.isFetching,
    installations: state.node.installations,
    currentTemperature: state.node.currentTemperatureForNode,
    currentRelativeHumidity: state.node.currentRelativeHumidityForNode,
    currentMonthCost: state.node.currentMonthCost,
    currentMonthEmission: state.node.currentMonthEmission,
  })
);

export const OverviewCard = () => {
  const {
    currency,
    isFetching,
    installations,
    currentTemperature,
    currentRelativeHumidity,
    currentMonthCost,
    currentMonthEmission,
  } = useSelector(getNodeSelector);
  const dispatch = useDispatch();

  const { domain, id }: any = useParams();
  const [informationModalOpened, setInformationModalOpened] = useState(false);
  const history = useHistory();

  if (isFetching)
    return (
      <Backdrop className={styles.backdrop} open={isFetching}>
        <CircularProgress color="secondary" />
      </Backdrop>
    );
  if (!installations) return <></>;

  const groupedInstallations = groupInstallations(installations).filter(
    (i) => i.DeviceType !== DeviceType.Climate && !isInstallationSensor(i)
  );

  const consumptionInstallations: IOverviewProgress[] =
    groupedInstallations.map((installation) => {
      const { currentData, comparisonData } = getDataForPrognosis(installation);
      const prognosis = getPrognosis(currentData, comparisonData);
      const util = getDisplayNameOnDeviceType(installation.DeviceType);
      const unit =
        installation.DeviceType === DeviceType.HeatCostAllocator
          ? ""
          : installation.data[0].Unit;

      return {
        key: installation.MeasuringDeviceID,
        util,
        unit,
        currentValue: Math.floor(getSumOfDataset(installation.data[0].Values)),
        goalValue: prognosis,
        onReadMore: () => goToDetailView(installation.DeviceType),
      };
    });

  const d = moment();

  const currentMonth = longMonthNames[d.month()];

  const goToDetailView = (deviceType: DeviceType) => {
    const currentMonthUnixTimeObject = getCurrentMonthUnixTimeObject();

    dispatch(
      setSelectedInstallation(
        domain,
        id,
        deviceType,
        currentMonthUnixTimeObject,
        dataInterval.Day
      )
    );
    history.push(`${domain ? `/${domain}` : ""}${id ? `/${id}` : ""}/details`);
  };
  return (
    <>
      <div className={styles.container}>
        {installations.find((i) => i.DeviceType === DeviceType.Climate) && (
          <Card onClick={() => goToDetailView(DeviceType.Climate)}>
            <div className={styles.row}>
              <Typography variant="subtitle2">{t("Temperature")}</Typography>
              {currentRelativeHumidity !== 0 && currentRelativeHumidity && (
                <Typography variant="subtitle2">
                  {t("Relative humidity")}
                </Typography>
              )}
            </div>
            <div className={styles.row}>
              <Typography variant="h1">
                {currentTemperature ? currentTemperature + "°C" : "-"}
              </Typography>
              {currentRelativeHumidity !== 0 && currentRelativeHumidity && (
                <Typography variant="h1">{currentRelativeHumidity}%</Typography>
              )}
            </div>
          </Card>
        )}

        {consumptionInstallations.length > 0 && (
          <Card>
            <div className={styles.row}>
              <Typography variant="h3">{currentMonth}</Typography>
              <Box>
                <InfoIcon
                  onClick={() => setInformationModalOpened(true)}
                  fontSize={"small"}
                />
              </Box>
            </div>
            <OverviewInformationModal
              open={informationModalOpened}
              onClose={() => setInformationModalOpened(false)}
            />
            <div className={styles.row}>
              <Typography variant="subtitle2">{t("Cost")}</Typography>
              <Typography variant="subtitle2">C02e</Typography>
            </div>
            <OverviewInformationModal
              open={informationModalOpened}
              onClose={() => setInformationModalOpened(false)}
            />
            <div className={styles.row}>
              <Typography variant="h1">
                {currentMonthCost}
                {currency}
              </Typography>
              <Typography variant="h1">
                {Math.round(currentMonthEmission * 10) / 10} kg
              </Typography>
            </div>
            {consumptionInstallations.map((installation, i) => {
              return (
                <OverviewGoalProgress
                  key={i}
                  util={installation.util}
                  unit={installation.unit}
                  currentValue={installation.currentValue}
                  goalValue={installation.goalValue}
                  onReadMore={installation.onReadMore}
                />
              );
            })}
          </Card>
        )}
      </div>
    </>
  );
};
